/**
 * Route-Service Mapping Configuration
 *
 * Phase 58: Scale-to-Zero with Boot Screen
 *
 * Current approach: System-wide boot
 * - All critical services wake together when any user logs in
 * - Provides predictable "platform waking up" experience
 * - Like a computer resuming from sleep
 *
 * Future: Feature-based warm-up (optional)
 * - ROUTE_SERVICE_REQUIREMENTS preserved for potential per-feature warm-up
 * - e.g., RAG chat wakes only langgraph, Talent Pipeline wakes only ingestion
 */

/**
 * Critical services that must be ready for the platform to function.
 * These are checked on ANY protected route access (system-wide boot).
 */
export const CRITICAL_SERVICES: string[] = ['langgraph', 'ingestion']

/**
 * [RESERVED FOR FUTURE USE]
 * Mapping of route patterns to required services.
 * Currently unused - system-wide boot checks all CRITICAL_SERVICES.
 * Preserved for potential feature-based warm-up policy.
 */
export const ROUTE_SERVICE_REQUIREMENTS: Record<string, string[]> = {
  // Chat requires langgraph for conversation and optionally ingestion for knowledge
  '/chat': ['langgraph'],

  // Talent pipeline requires ingestion service
  '/talent-pipeline/upload': ['ingestion'],
  '/talent-pipeline/positions': ['ingestion'],
  '/talent-pipeline': ['ingestion'],

  // Knowledge management (admin) requires ingestion
  '/knowledge': ['ingestion'],

  // Admin routes - no external services required (uses web-bff directly)
  '/admin': [],

  // Profile - no external services required
  '/profile': [],

  // Home page - no requirements
  '/': [],
}

/**
 * Get the required services for a given pathname.
 * Matches from most specific to least specific route patterns.
 *
 * @param pathname - The current route pathname
 * @returns Array of required service names
 */
export function getRequiredServices(pathname: string): string[] {
  // First, try exact match
  if (pathname in ROUTE_SERVICE_REQUIREMENTS) {
    return ROUTE_SERVICE_REQUIREMENTS[pathname]
  }

  // Then, try prefix match (most specific first)
  const sortedRoutes = Object.keys(ROUTE_SERVICE_REQUIREMENTS).sort(
    (a, b) => b.length - a.length
  )

  for (const route of sortedRoutes) {
    if (pathname.startsWith(route)) {
      return ROUTE_SERVICE_REQUIREMENTS[route]
    }
  }

  // No match - no requirements
  return []
}

/**
 * Spanish service display names for the boot screen
 */
export const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  session: 'Sesion',
  langgraph: 'Servicio de conversacion',
  ingestion: 'Servicio de candidatos',
  database: 'Base de datos',
}

/**
 * Get Spanish display name for a service
 */
export function getServiceDisplayName(service: string): string {
  return SERVICE_DISPLAY_NAMES[service] || service
}
